import { computed, unref, type Ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import BucketOption from "@/services/bucket/bucket-option";

export interface BucketOptionsParams {
  organizationId: string;
}

function isRef<T>(param: Ref<T> | T): param is Ref<T> {
  return typeof param === "object" && param !== null && "value" in param;
}

export function useBucketOptions(
  params: Ref<BucketOptionsParams | null> | BucketOptionsParams | null
) {
  const queryKey = computed(() => {
    const p = isRef(params) ? params.value : params;
    if (!p) return ["bucketOptions", "disabled"];
    return ["bucketOptions", p.organizationId];
  });

  const enabled = computed(() => {
    const p = isRef(params) ? params.value : params;
    return p !== null && p !== undefined && !!p.organizationId;
  });

  return useQuery({
    queryKey,
    queryFn: async () => {
      const p = isRef(params) ? params.value : params;
      if (!p) throw new Error("Parameters not ready");

      const result = await BucketOption.getBucketOptions({
        organizationId: p.organizationId,
      });

      if (result.data.status) {
        return result.data.data;
      }

      throw new Error(result.data.msg || "Failed to load bucket options");
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}
